import type { PageContext } from '../lib/types';
// Workspaces (IA §5, §36): work context and defaults. Not a brand or a folder of assets.
import { html, toast } from '../lib/dom';
import { icon } from '../components/icons';
import { coverThumb } from '../components/thumb';
import { bindCoverEditors, coverEditor } from '../components/entity-cover';
import { pageHead, tabs, projectCard, assetCard, runRow, kpi, table, status, empty, select, sectionHead } from '../components/ui';
import { openModal, confirmModal } from '../components/modal';
import { listWorkspaces, getWorkspace, workspaceBrands, listProjects, projectBrands, projectStats, createWorkspace, updateWorkspace, deleteWorkspace, currentUser, getProject } from '../api/workspace';
import { listAssets, assetCount, assetListState, loadMoreAssets, ownerLabel, SCOPE_LABEL } from '../api/library';
import { listRuns, runTotals } from '../api/runs';
import { loadMoreMarker, bindLoadMore } from '../components/load-more';
import { getFlow } from '../api/flows';
import { relTime, shortDate } from '../lib/format';
import { openNewProject } from './projects';
import * as store from '../store/ui';

export function renderList(ctx: PageContext) {
  const rows = listWorkspaces();
  const body = html`<div class="page narrow">
    ${pageHead({ title: 'Workspaces', lede: 'Where you work. Brands and Assets are shared across all workspaces.', actions: html`<button class="btn primary" data-new-ws>${icon('plus')}New workspace</button>` })}
    <div class="grid c2">
      ${rows.map((w?: any) => {
        const projects = listProjects({ workspaceId: w.id });
        const active = ctx.workspaceId === w.id;
        return html`<article class="card pad-lg col" style="gap:14px">
          ${coverEditor(w, 'workspace', 'workspace-card-cover')}
          <div class="row"><span class="avatar" style="width:40px;height:40px;background:var(--moss);color:var(--on-accent);font-size:15px">${w.name[0]}</span>
            <div class="col" style="gap:2px;flex:1"><a href="#/workspaces/${w.id}" style="font-size:18px;font-weight:500">${w.name}</a><span class="muted" style="font-size:13px">${w.description}</span></div></div>
          <div class="row mono muted wrap" style="font-size:11px;gap:14px"><span>${projects.length} projects</span><span>${workspaceBrands(w.id).length} brands</span><span>created ${shortDate(w.created_at)}</span></div>
          <div class="row"><a class="btn sm" href="#/workspaces/${w.id}">Open</a>${active ? html`<span class="chip solid">Current context</span>` : html`<button class="btn sm ghost" data-set-ws="${w.id}">Use as context</button>`}</div>
        </article>`;
      })}
    </div>
  </div>`;
  return {
    title: 'Workspaces', body,
    mount(root?: any) {
      bindCoverEditors(root);
      root.querySelector('[data-new-ws]')?.addEventListener('click', () => openModal({
        title: 'New workspace',
        body: html`<div class="form"><div class="field"><label>Name</label><input class="input" name="name" placeholder="e.g. Client Production" autofocus></div><div class="field"><label>Description</label><textarea class="textarea" name="description" rows="3"></textarea></div></div>`,
        foot: html`<button class="btn ghost" data-close>Cancel</button><button class="btn primary" data-create>Create</button>`,
        onMount(m?: any, close?: any) {
          m.querySelector('[data-create]').addEventListener('click', async () => {
            const name = m.querySelector('[name=name]').value.trim();
            if (!name) return;
            const button = m.querySelector('[data-create]');
            button.disabled = true;
            try {
              const id = await createWorkspace({ name, description: m.querySelector('[name=description]').value, userId: currentUser().id });
              close(); location.hash = `/workspaces/${id}`;
            } catch (error) { toast(error instanceof Error ? error.message : 'Workspace could not be saved'); }
            finally { button.disabled = false; }
          });
        },
      }));
    },
  };
}

const SECTIONS = [['general', 'General'], ['projects', 'Projects']];

function settings(w?: any, section?: any) {
  const activeSection = SECTIONS.some(([key]) => key === section) ? section : 'general';
  const content: any = {
    general: html`<form class="form" data-workspace-form><div class="field"><label>Name</label><input class="input" name="name" value="${w.name}" required></div><div class="field"><label>Description</label><textarea class="textarea" name="description" rows="3">${w.description}</textarea></div>
      <div><button class="btn primary" data-save>Save changes</button></div></form>`,
    projects: table([
      { label: 'Project', render: (p?: any) => p.name },
      { label: 'Status', render: (p?: any) => status(p.status) },
      { label: 'Updated', render: (p?: any) => relTime(p.updated_at) },
    ], listProjects({ workspaceId: w.id }), { rowHref: (p?: any) => `#/projects/${p.id}` }),
  };
  return html`<div class="settings-layout" data-local-tabs>
    <nav class="settings-nav">${SECTIONS.map(([k, l]: any) => html`<button class="side-link ${activeSection === k ? 'active' : ''}" data-param="section" data-value="${k}" style="border:none;background:${activeSection === k ? '' : 'transparent'};cursor:pointer;text-align:left">${l}</button>`)}</nav>
    <div class="card pad-lg">${content[activeSection]}</div>
  </div>`;
}

export function renderDetail(ctx: PageContext) {
  const w = getWorkspace(ctx.params.workspaceId);
  if (!w) return { title: 'Not found', body: empty('Workspace not found') };
  const tab = ['overview', 'projects', 'assets', 'settings'].includes(ctx.query.tab) ? ctx.query.tab : 'overview';
  const projects = listProjects({ workspaceId: w.id });
  const brands = workspaceBrands(w.id);
  const runs = listRuns({ workspaceId: w.id, limit: 5 });
  const runTotal = runTotals({ workspaceId: w.id });
  const assetFilters = { workspaceId: w.id, scope: ctx.query.scope };
  const assets = tab === 'assets' ? listAssets(assetFilters) : [];
  const isCtx = ctx.workspaceId === w.id;

  const content: any = {
    overview: html`<div class="grid c4">${kpi('Projects', projects.length)}${kpi('Active runs', runTotal.running, `${runTotal.runs} total`)}${kpi('Brands used', brands.length)}${kpi('Assets available', assetCount({ workspaceId: w.id }) ?? '…')}</div>
      <div class="split">
        <section class="section">${sectionHead('Projects', `#/workspaces/${w.id}?tab=projects`)}<div class="grid c2">${projects.slice(0, 4).map((p?: any) => projectCard(p, { workspace: w, brands: projectBrands(p.id), stats: projectStats(p.id) }))}</div></section>
        <div class="col" style="gap:16px">
          <section class="card pad"><h3 style="margin-bottom:10px">Brands in this workspace</h3>${brands.map((b?: any) => html`<a class="list-row" href="#/brands/${b.id}">${coverThumb(b)}<span class="t">${b.name}</span></a>`)}<p class="muted" style="font-size:12px;margin-top:8px">Brands are global — the same brand can be used in several workspaces.</p></section>
          <section class="card pad"><h3 style="margin-bottom:6px">Recent runs</h3>${runs.slice(0, 5).map((r?: any) => runRow(r, { flow: getFlow(r.flow_id), project: getProject(r.project_id) }))}</section>
        </div>
      </div>`,
    projects: html`<div class="toolbar"><span class="spacer"></span><button class="btn primary" data-new-project>${icon('plus')}New project</button></div><div class="grid auto">${projects.map((p?: any) => projectCard(p, { workspace: w, brands: projectBrands(p.id), stats: projectStats(p.id) }))}</div>`,
    assets: tab !== 'assets' ? '' : html`<div class="banner info">${icon('library')}<div>Everything <strong>available in ${w.name}</strong>: System, Organization, this workspace, its brands and projects. These assets are stored once and shown here as a filtered view. <a class="link" href="#/assets?workspace=${w.id}" style="color:var(--fern)">Open in Assets →</a></div></div>
      <div class="toolbar">${select('scope', Object.entries(SCOPE_LABEL), ctx.query.scope, { placeholder: 'All scopes' })}<span class="mono muted" style="font-size:11px">${assetCount(assetFilters) ?? '…'} assets</span></div>
      <div class="grid auto-sm">${assets.map((a?: any) => assetCard(a, ownerLabel(a)))}</div>${loadMoreMarker(assetListState(assetFilters), 'Loading assets…')}`,
    settings: settings(w, ctx.query.section || 'general'),
  };

  const body = html`<div class="page">
    <a data-back class="btn ghost sm" href="#/workspaces" style="align-self:flex-start">${icon('back')}Back</a>
    ${pageHead({ eyebrow: 'Workspace', title: w.name, lede: w.description, actions: html`${coverEditor(w, 'workspace', 'cover-editor-compact')}${isCtx ? html`<span class="chip solid">Current context</span>` : html`<button class="btn" data-set-ws="${w.id}">Use as working context</button>`}${currentUser()?.role === 'admin' ? html`<button class="btn danger" data-delete-workspace>${icon('trash')}Delete workspace</button>` : ''}` })}
    ${tabs([['overview', 'Overview'], ['projects', 'Projects', projects.length], ['assets', 'Assets'], ['settings', 'Settings']], tab)}
    ${content[tab]}
  </div>`;

  return {
    title: w.name, body,
    mount(root?: any) {
      bindCoverEditors(root);
      root.querySelector('[data-new-project]')?.addEventListener('click', (event: Event) => openNewProject({ workspaceId: w.id, returnFocus: event.currentTarget as HTMLElement }));
      root.querySelector('[data-workspace-form]')?.addEventListener('submit', async (event: Event) => {
        event.preventDefault();
        const form = event.currentTarget as HTMLFormElement;
        const button = form.querySelector<HTMLButtonElement>('[data-save]');
        if (button) button.disabled = true;
        try {
          await updateWorkspace(w.id, {
            name: (form.elements.namedItem('name') as HTMLInputElement).value,
            description: (form.elements.namedItem('description') as HTMLTextAreaElement).value,
          });
          toast('Workspace settings saved');
          ctx.rerender();
        } catch (error) { toast(error instanceof Error ? error.message : 'Workspace could not be saved'); }
        finally { if (button) button.disabled = false; }
      });
      root.querySelector('[data-delete-workspace]')?.addEventListener('click', () => confirmModal({
        title: `Delete “${w.name}”?`,
        text: `This permanently deletes this workspace and its ${projects.length} project(s), including their flows and run history. Asset files are kept. This cannot be undone.`,
        confirmLabel: 'Delete workspace', danger: true,
        onConfirm: async () => {
          await deleteWorkspace(w.id);
          if (store.get().workspaceId === w.id) store.set({ workspaceId: null });
          toast('Workspace deleted');
          location.hash = '/workspaces';
        },
      }));
      store.touchRecent('workspace', w.id);
      return tab === 'assets' ? bindLoadMore(root, assetListState(assetFilters), () => loadMoreAssets(assetFilters)) : undefined;
    },
  };
}
